/* eslint-disable react/jsx-props-no-spreading */
import { useDarkMode } from '@glu/theming';
import React, { MutableRefObject, useCallback, useContext } from 'react';
import { PanelOption } from '../Dropdown';
import PanelContext from '../PanelContext';
import Option from './Option';

export interface CustomOptionProps {
  focused?: boolean;
  handleKeyDown?(
    event: React.KeyboardEvent<HTMLInputElement>,
    option: PanelOption,
    isCheckbox: boolean
  ): void;
  htmlId?: string;
  isFocused?: boolean;
  key?: string;
  onClick?(option: PanelOption, isCategory: boolean): void;
  option: PanelOption;
  setFocus?(
    option: PanelOption,
    focused: boolean,
    optionRef: MutableRefObject<HTMLDivElement | null>
  ): void;
}

const Options = () => {
  const panelContext = useContext(PanelContext);
  const dark = useDarkMode();

  const {
    dropdownActions: { applySelection, setFocusedIndex, setIsFilterFocused, setSelection },
    dropdownProps: { htmlId, multiselect, optionComponent, options, showGroups },
    dropdownState: { focusedIndex, selection },
    panelActions: {
      handleCategorySelect,
      setArrowDirection,
      setIsActionsFocused,
      setParentName,
      setPath,
    },
    panelState: { filterString, path },
  } = panelContext;

  const getSelectionIds = useCallback(() => selection.map((object) => object.id), [selection]);
  const isInSelection = useCallback(
    (option) => getSelectionIds().indexOf(option.id) > -1,
    [getSelectionIds]
  );

  const handleCheckboxChange = useCallback(
    (option: PanelOption, checked) => {
      const newOption = { path, ...option };
      if (checked && !isInSelection(newOption)) {
        setSelection([...selection, newOption]);
      } else {
        setSelection(selection.filter((curr) => curr.id !== option.id));
      }
    },
    [isInSelection, path, selection, setSelection]
  );

  const hasChildren = (obj) => {
    if (obj.child && obj.child.length === 0) return false;
    if (obj.child) return true;
    return false;
  };

  const isFocusable = useCallback(
    (option) => !((showGroups && hasChildren(option)) || !!option.disabled),
    [showGroups]
  );

  const getNextFocusableItem = useCallback(
    (currentIndex) => {
      let newFocusedIndex = currentIndex;
      const optionIndex = newFocusedIndex - 1; /* currentIndex is one based! */
      for (let newIdx = optionIndex + 1; newIdx < options.length; newIdx += 1) {
        if (isFocusable(options[newIdx])) {
          newFocusedIndex = newIdx + 1; /* currentIndex is one based! */
          break;
        }
      }
      return newFocusedIndex;
    },
    [isFocusable, options]
  );

  const getPrevFocusHelper = useCallback(
    (currentIndex: number) => {
      let newFocusedIndex = currentIndex || 1;
      const optionIndex = newFocusedIndex - 1; /* currentIndex is one based! */
      for (let newIdx = optionIndex - 1; newIdx >= 0; newIdx -= 1) {
        if (isFocusable(options[newIdx])) {
          newFocusedIndex = newIdx + 1;
          break;
        }
      }
      return newFocusedIndex;
    },
    [isFocusable, options]
  );

  const getPrevFocusableItem = useCallback(
    (index: number) => {
      let newPrevItem = getPrevFocusHelper(index);
      if (newPrevItem === index) {
        setIsFilterFocused(true);
        newPrevItem = undefined;
      }
      return newPrevItem;
    },
    [getPrevFocusHelper, setIsFilterFocused]
  );

  const handleOptionClick = useCallback(
    (option, isCategory = false) => {
      if (hasChildren(option) && isCategory) {
        handleCategorySelect(option);
      } else {
        const newOption = { path, ...option };
        applySelection([newOption]);
      }
    },
    [applySelection, handleCategorySelect, path]
  );

  const handleKeyDown = useCallback(
    (event, option: PanelOption, isCheckbox: boolean) => {
      setIsActionsFocused(false);
      event.preventDefault();
      const { key, keyCode, shiftKey } = event;
      switch (true) {
        case key === 'ArrowUp':
          setArrowDirection('up');
          setFocusedIndex(getPrevFocusableItem(focusedIndex));
          break;
        case key === 'ArrowDown':
          setArrowDirection('down');
          setFocusedIndex(getNextFocusableItem(focusedIndex));
          break;
        case key === 'ArrowRight':
          if (hasChildren(option)) {
            handleCategorySelect(option);
            setArrowDirection('down');
            setFocusedIndex(getNextFocusableItem(0));
          }
          break;
        case key === 'ArrowLeft':
          setArrowDirection('down');
          if (path.length > 1) {
            setParentName(path[path.length - 2].id as string);
            setPath(path.slice(0, path.length - 1));
            setFocusedIndex(getPrevFocusHelper(focusedIndex));
          } else if (path.length) {
            setParentName('');
            setPath([]);
          }
          break;
        case key === 'Enter':
        case keyCode === 32:
          setArrowDirection('down');
          if (isCheckbox) {
            const {
              target: { checked },
            } = event;
            handleCheckboxChange(option, !checked);
          } else {
            handleOptionClick(option);
          }
          break;
        case !shiftKey && key === 'Tab':
          if (multiselect) {
            setIsActionsFocused(true);
          } else {
            setArrowDirection('down');
            setFocusedIndex(getNextFocusableItem(focusedIndex));
          }
          break;
        case shiftKey && key === 'Tab':
          if (multiselect) {
            setIsActionsFocused(false);
          } else {
            setArrowDirection('up');
            setFocusedIndex(getPrevFocusableItem(focusedIndex));
          }
          break;
        case key === 'Home':
          setArrowDirection('up');
          setFocusedIndex(1);
          break;
        case key === 'End':
          setArrowDirection('up');
          setFocusedIndex(options.length);
          break;
        default:
      }
    },
    [
      focusedIndex,
      getPrevFocusableItem,
      getNextFocusableItem,
      getPrevFocusHelper,
      handleCategorySelect,
      handleCheckboxChange,
      handleOptionClick,
      path,
      setArrowDirection,
      setFocusedIndex,
      setIsActionsFocused,
      setParentName,
      setPath,
      options,
      multiselect,
    ]
  );

  const setFocus = (option, focused, optionRef) => {
    // focusIndex is cleared every time the options are hidden
    // need to check what is selected on first render to highlight
    const selectedButNoFocusIndex =
      !focusedIndex && !multiselect && selection?.[0]?.id === option.id;
    if ((option.disabled || (hasChildren(option) && showGroups)) && focused) {
      setFocusedIndex(getNextFocusableItem(focusedIndex));
    } else if (focused || selectedButNoFocusIndex) {
      if (multiselect) {
        optionRef.current.querySelector('input').focus();
      } else {
        optionRef.current.focus();
        if (!focusedIndex) {
          setFocusedIndex(options.findIndex((opt) => opt.id === option.id) + 1);
        }
      }
    }
  };

  if (optionComponent) {
    const CustomOption = optionComponent;
    return (
      <>
        {options.map((option, idx) => (
          <CustomOption
            key={option.id as string}
            focused={focusedIndex === idx + 1}
            handleKeyDown={handleKeyDown}
            htmlId={htmlId}
            isFocused={focusedIndex === idx + 1}
            onClick={handleOptionClick}
            option={option}
            setFocus={setFocus}
          />
        ))}
      </>
    );
  }

  return (
    <>
      {options.map((option, idx) => (
        <Option
          key={option.id}
          dark={dark}
          filterString={filterString}
          handleKeyDown={handleKeyDown}
          htmlId={htmlId}
          isCategory={hasChildren(option)}
          isFocused={focusedIndex === idx + 1}
          isSelected={isInSelection(option)}
          multiselect={multiselect}
          onChange={(_name: string, checked: boolean) => {
            handleCheckboxChange(option, checked);
          }}
          onClick={handleOptionClick}
          option={option}
          setFocus={setFocus}
          showGroups={showGroups}
        />
      ))}
    </>
  );
};

export default Options;
